import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Box, Grid, IconButton } from "@mui/material";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import DashboardCard from "../../../components/DashboardCard";
import TrendIndicator from "../../TrendIndicator";
import ToolPerformanceBox from "./ToolPerformanceBox";

export default function ToolsPerformanceChart({
  toolsPerformance,
  height,
  shouldBeLoaded,
  link = "",
  showTitles = true,
  sx = {},
  trendData = {},
}) {
  const [chartOptions, setChartOptions] = useState({});
  const [sortedTools, setSortedTools] = useState({});
  const colors = useColors();
  const [loading, setLoading] = useState(true);
  const scrollContainer = useRef(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const navigate = useNavigate();

  const handleOnChartClick = (tool) => {
    navigate("/posture/controls", {
      state: { filters: { tools: { [tool]: true } } },
    });
  };

  useEffect(() => {
    if (Object.keys(toolsPerformance).length === 0) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      setSortedTools({});
    } else if (Object.keys(toolsPerformance).length > 0) {
      setShowRightArrow(true);
      setLoading(true);
      let newOptions = { ...chartOptions };

      // Convert object to array of [key, value] pairs and sort
      const sortedToolsPerformance = Object.entries(toolsPerformance)
        // Calculate percentage for each entry
        .map(([key, value]) => {
          // Assuming there are 'resolved' and 'total' properties
          const percentage =
            value.all > 0 ? (value.resolved / value.all) * 100 : 0;
          return {
            key,
            value,
            percentage,
          };
        })
        // Sort by percentage (lowest first)
        .sort((a, b) => a.percentage - b.percentage);

      // Convert sorted array back to object
      const sortedToolsObject = sortedToolsPerformance.reduce((obj, item) => {
        obj[item.key] = item.value;
        return obj;
      }, {});

      // Save the sorted object for rendering
      setSortedTools(sortedToolsObject);

      // Set up chart options for each tool
      sortedToolsPerformance.forEach((item) => {
        newOptions[item.key] = getChartOptions(item.key);
      });
      setChartOptions(newOptions);
    }
  }, [toolsPerformance]);

  useEffect(() => {
    if (shouldBeLoaded || Object.keys(chartOptions).length > 0) {
      setLoading(false);
    }
  }, [chartOptions, shouldBeLoaded]);

  useEffect(() => {
    // Show arrows if the user scrolls the container to the left or right manually
    if (scrollContainer.current) {
      scrollContainer.current.addEventListener("scroll", () => {
        setShowLeftArrow(scrollContainer.current.scrollLeft > 0);
        setShowRightArrow(
          scrollContainer.current.scrollLeft <
            scrollContainer.current.scrollWidth -
              scrollContainer.current.offsetWidth -
              10
        );
      });
    }
  }, [scrollContainer.current]);

  const scroll = (scrollOffset) => {
    const BUFFER = 10;
    scrollContainer.current.scrollLeft += scrollOffset;
    setShowLeftArrow(scrollContainer.current.scrollLeft > 0);
    setShowRightArrow(
      scrollContainer.current.scrollLeft <
        scrollContainer.current.scrollWidth -
          scrollContainer.current.offsetWidth -
          BUFFER
    );
  };

  const getCoverage = (toolName) => {
    return Math.round(
      (toolsPerformance[toolName].resolved / toolsPerformance[toolName].all) *
        100
    );
  };

  const getSeries = (tool) => ({
    Coverage: {
      number: getCoverage(tool),
    },
  });

  const getChartOptions = (tool) => {
    if (!tool) return {};
    const series = getSeries(tool);
    return {
      series: Object.values(series).map((data) => (data ? data.number : 0)),
      colors: Object.values(series).map((data) => {
        if (data.number > 70) {
          return colors.ops_heatmap_green_fill;
        } else if (data.number > 50) {
          return colors.ops_heatmap_orange_fill;
        } else {
          return colors.ops_heatmap_red_fill;
        }
      }),

      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val + "%";
        },
        style: {
          fontSize: "12px",
          fontFamily: "Poppins",
          fontWeight: "400",
        },
      },
      stroke: {
        lineCap: "round",
      },
      fill: {
        type: "solid",
      },

      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,

          dataLabels: {
            name: {
              show: false,
            },
            value: {
              offsetY: 0,
              fontSize: "18px",
              fontFamily: "Poppins",
              fontWeight: "600",
            },
          },
        },
      },
    };
  };

  return (
    <DashboardCard
      headline={showTitles ? "Security Controls Efficacy" : ""}
      subtitle={showTitles ? "How many controls are compliant" : ""}
      link={link}
      sx={sx}
    >
      {/* Chart part */}
      {loading ? (
        <Grid item>
          <Grid
            container
            height="240px"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingIcon />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "100%",
          }}
        >
          {showLeftArrow && (
            <IconButton
              onClick={() => scroll(-180)}
              sx={{
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <ArrowBackIosNewRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          )}
          <Box
            ref={scrollContainer}
            className="scroll-container"
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              alignItems: "center",
              overflowX: "scroll",
              width: "90%",
              margin: "0 auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "-ms-overflow-style": "none",
              scrollbarWidth: "none",
            }}
          >
            {Object.keys(sortedTools).length > 0
              ? Object.keys(sortedTools).map((tool) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {trendData && (
                      <TrendIndicator
                        data={trendData[tool]}
                        showPercent={true}
                      />
                    )}
                    <ToolPerformanceBox
                      key={tool}
                      compliantControls={toolsPerformance[tool]?.resolved}
                      allControls={toolsPerformance[tool]?.all}
                      tool={IntegrationsMapping[tool]}
                      highSeverity={toolsPerformance[tool]?.severity === "High"}
                      chartOptions={chartOptions[tool]}
                      onClick={() => handleOnChartClick(tool)}
                    />
                  </Box>
                ))
              : Object.keys(toolsPerformance).map((tool) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {trendData[tool] && (
                      <TrendIndicator
                        data={trendData[tool]}
                        showPercent={true}
                      />
                    )}
                    <ToolPerformanceBox
                      key={tool}
                      compliantControls={toolsPerformance[tool]?.resolved}
                      allControls={toolsPerformance[tool]?.all}
                      tool={IntegrationsMapping[tool]}
                      highSeverity={toolsPerformance[tool]?.severity === "High"}
                      chartOptions={chartOptions[tool]}
                      onClick={() => handleOnChartClick(tool)}
                    />
                  </Box>
                ))}
          </Box>
          {showRightArrow && (
            <IconButton
              onClick={() => scroll(180)}
              sx={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <ArrowForwardIosRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          )}
        </Box>
      )}
    </DashboardCard>
  );
}
